import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-center items-center gap-3 pb-3" }
const _hoisted_2 = { class: "flex justify-center items-center gap-3" }
const _hoisted_3 = { class: "flex justify-center items-center gap-6" }
const _hoisted_4 = { class: "flex justify-center items-center gap-1" }

import { colord } from "colord";
import { computed, ref, nextTick } from "vue";
import { PairSet } from "@/utils";
import { RGB8, rgb8AsArray, rgb8AsDict } from "@/keyboard";
import {
  useKeycapColorsStore,
  useKeyboardStore,
  useLayoutStore,
} from "@/stores";
import KeysLayout from "@/components/utils/KeysLayout.vue";
import ColorDot from "@/components/utils/ColorDot.vue";
import ColorInput from "@/components/utils/ColorInput.vue";
import KeyCap from "@/components/keys/KeyCap.vue";

type Key = ReturnType<typeof layout.layout.iter>[0];

export default /*@__PURE__*/_defineComponent({
  __name: 'KeycapsArea',
  setup(__props) {

const colors = useKeycapColorsStore();
const layout = useLayoutStore();
const kb = useKeyboardStore();

const keycaps = ref<InstanceType<typeof KeyCap>[]>([]);
const selected = ref<PairSet>(new PairSet());

// Channing selected may change current color, which in turn sets this color for all selected
// items. It e.g. happens when clicking the selection buttons (all/left/right) or generating
// random colors. For this reason after such operations we need to block a single setColor.
let blockSetColor = false;
const blockNextSetColor = () => {
  blockSetColor = true;
  nextTick(() => nextTick(() => (blockSetColor = false)));
};

const firstSelected = computed(() => {
  const first = selected.value.iter()[0];
  if (first == undefined) return;
  return keycaps.value.find(
    (k) => k.$props.row == first[0] && k.$props.col == first[1]
  );
});

const currentColor = computed((): [number, number, number] => {
  const keycap = firstSelected.value;
  if (keycap == undefined) return [0, 0, 0];
  return rgb8AsArray(colors.at(keycap.$props.row, keycap.$props.col).toRgb());
});

const setColor = (rgb: RGB8) => {
  if (blockSetColor) {
    return;
  }
  for (const [row, col] of selected.value.iter()) {
    const keycap = keycaps.value.find(
      (k) => k.$props.row == row && k.$props.col == col
    );
    if (keycap != undefined) {
      colors.setKey(
        keycap.$props.row,
        keycap.$props.col,
        colord(rgb8AsDict(rgb))
      );
    }
  }
};

const setAll = (isSelected: (key: Key) => boolean) => {
  blockNextSetColor();
  layout.layout
    .iter()
    .forEach((key) =>
      isSelected(key)
        ? selected.value.add(key.row, key.col)
        : selected.value.remove(key.row, key.col)
    );
};

const setRandom = () => {
  blockNextSetColor();
  const selection = selected.value.iter();
  const at = selection.length > 0 ? selection : undefined;
  colors.setRandom(at);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(KeysLayout, { class: "my-4" }, {
      default: _withCtx((s) => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(s.keys, (k, id) => {
          return (_openBlock(), _createBlock(KeyCap, {
            key: id,
            ref_for: true,
            ref_key: "keycaps",
            ref: keycaps,
            class: "keycap-no-transitions",
            row: k.row,
            col: k.col,
            x: k.key.x,
            y: k.key.y,
            rot: k.key.rot,
            height: k.key.size.y,
            width: k.key.size.x,
            blank: true,
            outlined: selected.value.has(k.row, k.col),
            onClick: 
            () => {
              blockNextSetColor();
              selected.value.toggle(k.row, k.col);
            }
          
          }, null, 8, ["row", "col", "x", "y", "rot", "height", "width", "outlined", "onClick"]))
        }), 128))
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(colors).defaultColors, (c, id) => {
        return (_openBlock(), _createBlock(ColorDot, {
          key: id,
          color: c.toHex(),
          onClick: () => setColor(_unref(rgb8AsArray)(c.toRgb()))
        }, null, 8, ["color", "onClick"]))
      }), 128)),
      _createVNode(ColorInput, {
        class: _normalizeClass(selected.value.count == 0 ? 'pointer-events-none opacity-40' : ''),
        modelValue: currentColor.value,
        "onUpdate:modelValue": setColor
      }, null, 8, ["class", "modelValue"]),
      _createElementVNode("button", {
        class: "btn btn-accent",
        onClick: setRandom
      }, "Random")
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("button", {
          class: "btn btn-neutral",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (setAll(() => true)))
        }, "All"),
        _createElementVNode("button", {
          class: "btn btn-neutral",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (setAll(() => false)))
        }, " Clear "),
        _createElementVNode("button", {
          class: "btn btn-neutral",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (setAll((k) => k.col < _unref(kb).columns / 2)))
        }, " Left "),
        _createElementVNode("button", {
          class: "btn btn-neutral",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (setAll((k) => k.col >= _unref(kb).columns / 2)))
        }, " Right ")
      ])
    ])
  ]))
}
}

})